import React, { useState, createContext, useContext, useRef } from 'react';
import classNames from 'classnames';
import { Icon } from '../Icon/Icon';
import uuidv4 from '../../utils/uuidv4';
const WizardContext = createContext(undefined);
/**
 * Bronson Wizard component.
 * @see https://bronson.vwfs.io/default/components/detail/bronson-wizard.html
 * @constructor
 */
export function Wizard({ ariaLabel, children, className, collapseBreakpoint, collapsedCounter, collapsedText, collapsedTitle, defaultOpen = false, noInteraction, stepLabels, testId, ...otherProps }) {
    const [open, setOpen] = useState(defaultOpen);
    const navClassNameList = classNames('c-wizard', 'js-is-ready', // Used in Bronson CSS.
    {
        'c-wizard--no-interaction': noInteraction, // Convenience prop from Bronson variants.
    }, className).trim();
    const olClassNameList = classNames('c-wizard__list', {
        'is-closed': !open,
    }, className).trim();
    return (React.createElement("nav", { "aria-label": ariaLabel, className: navClassNameList, "data-wizard-collapse": collapseBreakpoint, "data-testid": testId, ...otherProps },
        React.createElement("button", { className: "c-wizard__trigger", type: "button", "aria-expanded": open, title: collapsedTitle, onClick: () => setOpen(!open) },
            React.createElement(Icon, { className: "c-wizard__trigger-icon", viaCss: true }),
            collapsedText && React.createElement("span", { className: "c-wizard__trigger-text" }, collapsedText),
            collapsedCounter && React.createElement("span", { className: "c-wizard__trigger-counter" }, collapsedCounter)),
        React.createElement(WizardContext.Provider, { value: { stepLabels } },
            React.createElement("ol", { className: olClassNameList }, children))));
}
Wizard.displayName = 'Wizard';
/**
 * Bronson WizardItem component (nested).
 * @constructor
 */
function WizardItem({ active, children, className, done, error, noInteraction, onClick, testId, ...otherProps }) {
    const context = useContext(WizardContext);
    if (!context) {
        console.warn('WizardItem must be used within a Wizard');
        return null;
    }
    const { stepLabels } = context;
    const liClassNameList = classNames('c-wizard__item', {
        'is-done': done,
        'is-active': active,
        'is-error': error,
        'is-regular': !(active || error || done || noInteraction),
        'has-no-interaction': noInteraction,
    }, className).trim();
    const getLabel = () => {
        if (done)
            return stepLabels.doneLabel;
        if (noInteraction)
            return stepLabels.noInteractionLabel;
        if (error)
            return stepLabels.errorLabel;
        return stepLabels.notDoneLabel;
    };
    const ariaCurrent = active ? 'step' : undefined;
    const titleId = useRef(uuidv4('wizard-item-title-')).current;
    const statusId = useRef(uuidv4('wizard-item-status-')).current;
    const statusLabel = ariaCurrent ? undefined : getLabel();
    return (React.createElement("li", { "data-testid": testId, className: liClassNameList },
        React.createElement("a", { "aria-current": ariaCurrent, "aria-labelledby": statusLabel ? `${statusId} ${titleId}` : titleId, className: "c-wizard__link", onClick: onClick, ...otherProps },
            statusLabel && (React.createElement("span", { id: statusId, className: "u-visually-hidden" }, statusLabel)),
            React.createElement("span", { id: titleId, className: "c-wizard__link-title" }, children))));
}
WizardItem.displayName = 'Wizard.Item';
Wizard.Item = WizardItem;
